import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ButtonBase, Skeleton } from '@mui/material';

import SeoOnlyLink from 'components/utils/SeoOnlyLink';
import CardSearch from 'components/navigation/card-search';

import { useQueryDict, useCardNav } from 'hooks/app';

import './styles.scss';

const CardNavigation = () => {
  const navigate = useNavigate();
  const { pathname, updateSearch } = useQueryDict();
  const subcategoriesMenuRef = useRef();
  const hoveredTimeout = useRef();
  const [hovered, setHovered] = useState(-1);
  const [open, setOpen] = useState(false);
  const { navItems, loading } = useCardNav();

  const currentNavItem = hovered >= 0 && navItems?.length > 0 ? navItems[hovered] : null;

  const handleHovered = (index) => {
    if (index === -1) {
      return false;
    } else {
      if (!open) {
        handleOpen();
      }
      setHovered(index);
    }
  }

  const debounce = (func, timeout = 300) => {
    return (...args) => {
      clearTimeout(hoveredTimeout.current);
      hoveredTimeout.current = setTimeout(() => { func.apply(this, args); }, timeout);
    };
  }

  const debouncedHandleHovered = debounce((index) => {
    handleHovered(index);
  }, 120);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 40) {
      subcategoriesMenuRef && subcategoriesMenuRef.current && subcategoriesMenuRef.current.focus();
      e.preventDefault();
      return false;
    }
  }

  const handleLeave = () => {
    clearTimeout(hoveredTimeout.current);
    setHovered(-1);
    handleClose();
  }

  const handleItemClick = (c) => {
    if (hovered >= 0) {
      handleClose();
      navigate(c.fullCardCategorySlug);
    }
    handleClose();
  }

  const onSearchChange = (value) => {
    if (value) {
      navigate(`/cards${updateSearch({ 'search_tx': value })}`);
    } else {
      navigate(`${pathname}${updateSearch({ 'search_tx': undefined })}`);
    }
  }

  //logInfo('RENDER: Card Navigation');

  if (loading) {
    return (
      <nav className="SSR-card-navigation card-navigation">
        <div style={{ height: 60, display: 'flex', justifyContent: 'center' }}>
          <Skeleton width={85} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={70} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={115} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={160} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={120} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={102} height={22} style={{ alignSelf: 'center', margin: '0 1rem' }} animation="wave"/>
          <Skeleton width={220} height={22} style={{ alignSelf: 'center', marginLeft: '1rem' }} animation="wave"/>
        </div>
      </nav>
    )
  }

  return (
    <nav
      className="SSR-card-navigation card-navigation"
      aria-controls={open ? 'card-navigation-menu' : undefined}
      aria-haspopup="true"
      onMouseLeave={handleLeave}
      style={{ position: 'relative' }}
    >
      {navItems?.length > 1 &&
        <ul className="card-categories">
          {navItems.map((c, i) => (
            <li
              key={`nav-category-${i + 1}`}
              name={c.shortCategoryName}
              className={`card-category card-category-${i} ${(c.fullCardCategorySlug === pathname || hovered === i) ? 'selected' : ''}`}
              onClick={() => handleItemClick(c)}
              onMouseEnter={() => debouncedHandleHovered(i)}
              onFocus={() => debouncedHandleHovered(i)}
              onKeyDown={handleKeyDown}
            >
              <ButtonBase tabIndex={-1} focusRipple>
                <SeoOnlyLink
                  to={c.fullCardCategorySlug}
                  className="card-category-name"
                  alt={`${c.shortCategoryName} Cards`}
                  title={`${c.shortCategoryName} Cards`}
                >
                  {c.shortCategoryName}
                </SeoOnlyLink>
              </ButtonBase>
            </li>
          ))}
          <li className="card-category-search">
            <CardSearch onSearchChange={onSearchChange} placeholder="Search all cards"/>
          </li>
        </ul>
      }
      {hovered >= 0 && currentNavItem && open &&
        <div className="card-navigation-menu" id="card-navigation-menu" onMouseEnter={() => clearTimeout(hoveredTimeout.current)}>
          <div className="card-navigation-menu-wrapper">
            <div className={`card-category-children ${currentNavItem?.cardCategoryId === 5 && 'flex-column'}`}>
              {currentNavItem?.children?.map((c, i) => (
                <Link
                  className="card-category-child"
                  key={`card-category-child-${i}`}
                  to={c.fullCardCategorySlug}
                  title={c.shortCategoryName}
                  onClick={handleClose} ref={i === 0 ? subcategoriesMenuRef : undefined}
                >
                  <span className="child-name">{c.shortCategoryName}</span>
                  {c.nextDate && <span className="child-info">{c.nextDate}</span>}
                </Link>
              ))}
            </div>
            {currentNavItem?.images?.length > 0 &&
              <div className="card-category-images">
                {currentNavItem?.images?.map((image, i) => (
                  <div className="image-item" key={`image-${i+1}`} onClick={handleClose}>
                    <Link to={image.url}>
                      <img src={image.s3Uri} alt={image.imgAlt} width="150" height="194"/>
                    </Link>
                  </div>
                ))}
              </div>
            }
          </div>
        </div>
      }
    </nav>
  );
};

export default CardNavigation;
